import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAiDictationStore from '../stores/aiDictationStore';
import StatusBar from '../components/StatusBar';

const AiDictationStart = () => {
  const navigate = useNavigate();
  const { selectedWords, generatedStory, dictationSettings } = useAiDictationStore();
  
  // 听写状态
  const [currentStep, setCurrentStep] = useState('ready'); // ready, dictating, writing, result
  const [countdown, setCountdown] = useState(3);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [results, setResults] = useState([]);
  
  // 解析短文为句子
  const sentences = React.useMemo(() => {
    // 简单的句子分割，实际应用中可能需要更复杂的处理
    const text = generatedStory.text.replace(/<[^>]*>/g, ''); // 移除HTML标签
    return text.split(/(?<=[.!?])\s+/);
  }, [generatedStory.text]);
  
  // 解析短文中的目标单词
  const targetWords = React.useMemo(() => {
    const matches = generatedStory.text.match(/<span class="word-highlight">([^<]+)<\/span>/g) || [];
    return matches.map(match => match.replace(/<[^>]*>/g, '').trim());
  }, [generatedStory.text]);
  
  // 倒计时效果
  useEffect(() => {
    if (currentStep === 'ready' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (currentStep === 'ready' && countdown === 0) {
      setCurrentStep('dictating');
    }
  }, [currentStep, countdown]);
  
  // 处理用户输入
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };
  
  // 提交当前单词
  const handleSubmit = () => {
    // 检查答案
    const isCorrect = userInput.toLowerCase().trim() === targetWords[currentSentenceIndex].toLowerCase();
    
    // 更新结果
    setResults([...results, {
      sentence: sentences[currentSentenceIndex],
      targetWord: targetWords[currentSentenceIndex],
      userInput: userInput.trim(),
      isCorrect
    }]);
    
    // 清空输入
    setUserInput('');
    
    // 移动到下一个句子或结束
    if (currentSentenceIndex < sentences.length - 1) {
      setCurrentSentenceIndex(currentSentenceIndex + 1);
    } else {
      setCurrentStep('result');
    }
  };
  
  // 返回上一页
  const handleBack = () => {
    navigate('/ai-dictation');
  };
  
  // 重新开始
  const handleRestart = () => {
    // 重置所有状态
    setCurrentStep('ready');
    setCountdown(3);
    setCurrentSentenceIndex(0);
    setUserInput('');
    setResults([]);
  };
  
  // 渲染准备页面
  const renderReadyScreen = () => (
    <div className="flex flex-col items-center justify-center h-[70vh]">
      <div className="text-5xl font-bold text-indigo-500 mb-8">{countdown}</div>
      <h2 className="text-2xl font-bold mb-4">准备开始听写</h2>
      <p className="text-gray-500 text-center mb-8">
        请准备好纸笔，听到句子后在下方输入框中拼写目标单词
      </p>
      <div className="text-center">
        <p className="text-sm text-gray-400 mb-2">听写设置</p>
        <p className="text-sm text-gray-500">单词数量: {targetWords.length}个</p>
        <p className="text-sm text-gray-500">朗读速度: {
          dictationSettings.readingSpeed === 'slow' ? '慢速' :
          dictationSettings.readingSpeed === 'fast' ? '快速' : '正常'
        }</p>
        <p className="text-sm text-gray-500">重复次数: {dictationSettings.repeatCount}次</p>
      </div>
    </div>
  );
  
  // 渲染听写页面
  const renderDictationScreen = () => (
    <div className="flex flex-col items-center justify-center h-[70vh]">
      <div className="mb-8">
        <div className="text-sm text-gray-500 mb-2 text-center">
          正在听写第 {currentSentenceIndex + 1}/{sentences.length} 个单词
        </div>
        <div className="flex space-x-1">
          {targetWords.map((_, index) => (
            <div 
              key={index}
              className={`w-2 h-2 rounded-full ${
                index < currentSentenceIndex 
                  ? (results[index]?.isCorrect ? 'bg-green-500' : 'bg-red-500')
                  : index === currentSentenceIndex 
                    ? 'bg-indigo-500' 
                    : 'bg-gray-200'
              }`}
            ></div>
          ))}
        </div>
      </div>
      
      <div className="w-full max-w-md mb-8">
        <input
          type="text"
          value={userInput}
          onChange={handleInputChange}
          className="w-full p-4 text-center text-xl border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="请输入听到的单词"
          autoFocus
        />
      </div>
      
      <button 
        className="bg-indigo-500 text-white py-3 px-8 rounded-xl font-bold text-lg shadow-md"
        onClick={handleSubmit}
      >
        提交
      </button>
    </div>
  );
  
  // 渲染结果页面
  const renderResultScreen = () => {
    const correctCount = results.filter(result => result.isCorrect).length;
    const accuracy = Math.round((correctCount / results.length) * 100);
    
    return (
      <div className="flex flex-col items-center justify-center h-[70vh]">
        <div className="mb-6">
          <div className="text-center mb-2">
            <span className="text-4xl font-bold text-indigo-500">{accuracy}%</span>
          </div>
          <p className="text-gray-500 text-center">
            共{results.length}个单词，正确{correctCount}个
          </p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-4 w-full max-w-md mb-8">
          <h3 className="font-bold text-lg mb-3">听写结果</h3>
          <div className="space-y-2">
            {results.map((result, index) => (
              <div 
                key={index}
                className={`p-2 rounded-lg ${
                  result.isCorrect ? 'bg-green-50' : 'bg-red-50'
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium">{result.targetWord}</span>
                  <span className={`text-sm ${
                    result.isCorrect ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {result.isCorrect ? '正确' : '错误'}
                  </span>
                </div>
                {!result.isCorrect && (
                  <div className="text-sm text-red-600">
                    你的答案: {result.userInput}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {result.sentence}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex space-x-4">
          <button 
            className="bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-bold shadow-md"
            onClick={handleBack}
          >
            返回
          </button>
          <button 
            className="bg-indigo-500 text-white py-3 px-6 rounded-xl font-bold shadow-md"
            onClick={handleRestart}
          >
            重新开始
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">
      {/* Header */}
      <div className="p-5">
        <div className="flex justify-between items-center">
          <button 
            className="text-indigo-500"
            onClick={handleBack}
          >
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
          <h2 className="text-xl font-bold">AI短文听写</h2>
          <div className="w-8"></div> {/* Spacer for alignment */}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="p-5">
        {currentStep === 'ready' && renderReadyScreen()}
        {currentStep === 'dictating' && renderDictationScreen()}
        {currentStep === 'result' && renderResultScreen()}
      </div>
    </div>
  );
};

export default AiDictationStart;